import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function ServerControl() {
  const toast = useToast();
  const [servers, setServers] = useState([]);
  const [server, setServer] = useState("");
  const [rcon, setRcon] = useState("");
  const [rconResp, setRconResp] = useState(null);
  const [broadcast, setBroadcast] = useState("");
  const [kickForm, setKickForm] = useState({ player_id: "", reason: "Kicked by admin" });
  const [shutForm, setShutForm] = useState({ delay_seconds: 60, message: "Server is shutting down" });
  const [restartDelay, setRestartDelay] = useState(60);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setServer(list[0].name || list[0].slug);
    }).catch(() => {});
  }, []);

  const send = async (label, fn) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(`${label}: ${r.data?.ok ?? "sent"}`);
      return r.data;
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 fade-up" data-testid="server-control-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Server Control</h3>
        <p className="text-xs text-[var(--muted)]">
          Send live commands to a Palworld server. <strong>shutdown</strong> / <strong>restart</strong> notify players via the in-game broadcast. <strong>kick</strong> uses the REST API; everything else is RCON.
        </p>
      </div>

      <div className="glass-card p-4">
        <Field label="Target server">
          <select className="input" value={server} onChange={(e) => setServer(e.target.value)} data-testid="server-control-select">
            {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <form onSubmit={async (e) => { e.preventDefault(); const r = await send("RCON", () => api.post("/admin/server/rcon", { server_name: server, command: rcon })); setRconResp(r); }} className="glass-card p-5 space-y-3" data-testid="rcon-form">
          <h3 className="label">Raw RCON</h3>
          <Field label="Command"><input className="input font-mono text-xs" required value={rcon} onChange={(e) => setRcon(e.target.value)} placeholder="ShowPlayers" data-testid="rcon-command" /></Field>
          <button className="btn" disabled={busy} data-testid="rcon-submit">Send</button>
          {rconResp && (
            <pre className="text-[10px] bg-black/40 rounded p-2 overflow-auto max-h-32 mt-2">{JSON.stringify(rconResp, null, 2)}</pre>
          )}
        </form>

        <form onSubmit={async (e) => { e.preventDefault(); await send("Broadcast", () => api.post("/admin/server/announce", { server_name: server, message: broadcast })); setBroadcast(""); }} className="glass-card p-5 space-y-3" data-testid="broadcast-form">
          <h3 className="label">Broadcast Message</h3>
          <Field label="Message"><input className="input" required maxLength={400} value={broadcast} onChange={(e) => setBroadcast(e.target.value)} placeholder="Hello world!" data-testid="broadcast-message" /></Field>
          <button className="btn" disabled={busy || !broadcast} data-testid="broadcast-submit">Send</button>
          <p className="text-[10px] text-[var(--muted)]">Spaces will be auto-converted to underscores (Palworld requirement).</p>
        </form>

        <form onSubmit={async (e) => { e.preventDefault(); if (!window.confirm(`Kick ${kickForm.player_id}?`)) return; await send("Kick", () => api.post("/admin/server/kick", { server_name: server, ...kickForm })); }} className="glass-card p-5 space-y-3" data-testid="kick-form">
          <h3 className="label">Kick Player</h3>
          <Field label="Player UID"><input className="input font-mono text-xs" required value={kickForm.player_id} onChange={(e) => setKickForm({ ...kickForm, player_id: e.target.value })} data-testid="kick-player-id" /></Field>
          <Field label="Reason"><input className="input" value={kickForm.reason} onChange={(e) => setKickForm({ ...kickForm, reason: e.target.value })} data-testid="kick-reason" /></Field>
          <button className="btn btn-danger" disabled={busy} data-testid="kick-submit">Kick</button>
        </form>

        <form onSubmit={async (e) => { e.preventDefault(); if (!window.confirm(`Shutdown ${server} in ${shutForm.delay_seconds}s?`)) return; await send("Shutdown", () => api.post("/admin/server/shutdown", { server_name: server, ...shutForm, delay_seconds: parseInt(shutForm.delay_seconds, 10) })); }} className="glass-card p-5 space-y-3" data-testid="shutdown-form">
          <h3 className="label">Graceful Shutdown</h3>
          <Field label="Delay (seconds)"><input className="input" type="number" min="1" max="3600" required value={shutForm.delay_seconds} onChange={(e) => setShutForm({ ...shutForm, delay_seconds: e.target.value })} data-testid="shutdown-delay" /></Field>
          <Field label="Message"><input className="input" required value={shutForm.message} onChange={(e) => setShutForm({ ...shutForm, message: e.target.value })} data-testid="shutdown-message" /></Field>
          <button className="btn btn-danger" disabled={busy} data-testid="shutdown-submit">Shutdown</button>
        </form>

        <form onSubmit={async (e) => { e.preventDefault(); if (!window.confirm(`Restart ${server}? (~${restartDelay}s + restart time)`)) return; await send("Restart", () => api.post("/admin/server/restart", { server_name: server, delay_seconds: parseInt(restartDelay, 10) })); }} className="glass-card p-5 space-y-3" data-testid="restart-form">
          <h3 className="label">Restart</h3>
          <Field label="Delay (seconds)"><input className="input" type="number" min="1" max="3600" required value={restartDelay} onChange={(e) => setRestartDelay(e.target.value)} data-testid="restart-delay" /></Field>
          <p className="text-[10px] text-[var(--muted)]">Broadcasts countdown, saves world, sends DoExit. Server auto-relaunch must be configured separately.</p>
          <button className="btn btn-danger" disabled={busy} data-testid="restart-submit">Restart</button>
        </form>

        <form onSubmit={async (e) => { e.preventDefault(); await send("Save", () => api.post(`/admin/server/save?server_name=${encodeURIComponent(server)}`)); }} className="glass-card p-5 space-y-3" data-testid="save-form">
          <h3 className="label">Save World</h3>
          <p className="text-xs text-[var(--muted)]">Forces an immediate world save via RCON.</p>
          <button className="btn" disabled={busy} data-testid="save-submit">Save Now</button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Permanent In-game Admins
// ---------------------------------------------------------------------------
